import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation  } from "react-router-dom";

import {
  collection,
  doc,
  serverTimestamp,
  setDoc,
  onSnapshot,
  increment
} from "firebase/firestore";

import { db } from "../firebaseConfig";
import { getToken } from "firebase/app-check";
import { appCheck } from "../firebaseConfig";

import "../styles/generalStyle.css";
import "./CreateSongIdeas.css";
import "./Landing.css";
import { useLanguage } from "../i18n/LanguageContext";
import logoMark from "../assets/landing/logo_512.png";
import LanguageDropdown from "../components/LanguageDropdown";




const GENERAR_LETRA_URL = "https://us-central1-cancionesia.cloudfunctions.net/generarLetra";
const GENERAR_LETRA_URL_CHECK = "https://us-central1-cancionesia.cloudfunctions.net/generarLetraCheck";

const DRAFT_KEY = "songWizardDraft_v1";

const GUEST_LETRAS_LIMIT = 3;
const GUEST_COOLDOWN_MS = 600_000; // 10 min entre generaciones (anti spam)
const GUEST_KEY = "guestLyricsLimits_v1";

const GUEST_SONGID_KEY = "guest_createdSongId_v1";

const FORM_EXPIRE_KEY = "songWizardExpireAt_v1";


function getOrCreateGuestId() {
  const KEY = "mac_guest_id";
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = (crypto?.randomUUID?.() || `g_${Date.now()}_${Math.random().toString(16).slice(2)}`);
    localStorage.setItem(KEY, id);
  }
  return id;
}

function loadExpireAt() {
  try {
    const raw = localStorage.getItem(FORM_EXPIRE_KEY);
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}
function saveExpireAt(ts) {
  try { localStorage.setItem(FORM_EXPIRE_KEY, String(ts)); } catch {}
}
function clearExpireAt() {
  try { localStorage.removeItem(FORM_EXPIRE_KEY); } catch {}
}

function clearGuestSongId() {
  try { sessionStorage.removeItem(GUEST_SONGID_KEY); } catch {}
}

function saveGuestSongId(songId) {
  try { sessionStorage.setItem(GUEST_SONGID_KEY, songId || ""); } catch {}
}

function loadGuestSongId() {
  try { return sessionStorage.getItem(GUEST_SONGID_KEY) || ""; } catch { return ""; }
}

function loadGuestLimits() {
  try {
    const raw = sessionStorage.getItem(GUEST_KEY);
    return raw ? JSON.parse(raw) : { left: GUEST_LETRAS_LIMIT, lastAt: 0 };
  } catch {
    return { left: GUEST_LETRAS_LIMIT, lastAt: 0 };
  }
}
function saveGuestLimits(v) {
  try {
    sessionStorage.setItem(GUEST_KEY, JSON.stringify(v));
  } catch {}
}
function resetGuestLimits() {
  try {
    sessionStorage.removeItem(GUEST_KEY);
  } catch {}
}

function loadDraft() {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function saveDraft(draft) {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {}
}
function clearDraft() {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {}
}

// guestId seguro para docId (sin usar el email directo)
const toGuestId = (email) =>
  (email || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "_")
    .slice(0, 120);

const defaultDraft = {
  step: 0,

  // step 1
  estilo: "",
  instrumento: "",
  voz: "hombre",

  // step 2
  ocasion: "",

  // step 3
  animo: "",

  // step 4
  fechas: "",
  eventos: "",

  // step 5
  nombres: "",

  // step 6
  historia: "",

  // step 7
  extras: "",

  // step 8
  letra: "",
  prompt: "",
  generatedAt: null
};

const CountdownBanner = React.memo(function CountdownBanner({ t, expireAt, onReset }) {
  const [now, setNow] = React.useState(Date.now());

  React.useEffect(() => {
    if (!expireAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expireAt]);

  if (!expireAt) return null;

  const msLeft = Math.max(0, expireAt - now);
  const expired = msLeft <= 0;

  const hh = String(Math.floor(msLeft / 3600000)).padStart(2, "0");
  const mm = String(Math.floor((msLeft % 3600000) / 60000)).padStart(2, "0");
  const ss = String(Math.floor((msLeft % 60000) / 1000)).padStart(2, "0");

  return (
    <div
      style={{
        position: "relative",
        marginBottom: 12,
        padding: 14,
        borderRadius: 18,
        border: "1px solid rgba(59, 130, 246, 0.35)",

        // azul difuminado + glass
        background:
          "radial-gradient(1200px 200px at 10% 0%, rgba(59,130,246,0.35), transparent 55%)," +
          "radial-gradient(900px 220px at 90% 100%, rgba(147,197,253,0.20), transparent 55%)," +
          "rgba(255,255,255,0.04)",
        backdropFilter: "blur(10px)",

        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        flexWrap: "wrap",
        overflow: "hidden",
      }}
    >
      {/* brillo suave */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: -2,
          pointerEvents: "none",
          background:
            "linear-gradient(90deg, rgba(59,130,246,0.10), rgba(255,255,255,0.04), rgba(59,130,246,0.08))",
          opacity: 0.6,
          filter: "blur(14px)",
        }}
      />

      <div style={{ position: "relative", minWidth: 240 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <span
            style={{
              fontSize: 12,
              fontWeight: 900,
              padding: "4px 10px",
              borderRadius: 999,
              border: "1px solid rgba(59,130,246,0.35)",
              background: "rgba(59,130,246,0.15)",
            }}
          >
            {expired ? "⏳" : "⚡"}
            <span style={{ marginLeft: 6 }}>
              {expired ? t("pay.timerExpiredChip", "Expirado") : t("pay.timerChip", "Tiempo activo")}
            </span>
          </span>
        </div>

        <div style={{ fontWeight: 950, fontSize: 15 }}>
          {expired
            ? t("pay.timerExpiredTitle", "El tiempo terminó")
            : t("pay.timerTitle", "Tu canción ya está lista para asegurar")}
        </div>

      </div>

      <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 12 }}>
        <div
          style={{
            fontWeight: 950,
            fontSize: 22,
            letterSpacing: 2,
            padding: "8px 12px",
            borderRadius: 14,
            border: "1px solid rgba(255,255,255,0.10)",
            background: "rgba(0,0,0,0.18)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {expired ? "00:00:00" : `${hh}:${mm}:${ss}`}
        </div>

        {/* dejamos la opción, pero tú no lo usarás por ahora
        {onReset ? (
          <button className="btn" type="button" onClick={onReset}>
            {t("pay.timerReset", "Reiniciar")}
          </button>
        ) : null}
          */}
      </div>
    </div>
  );
});

export default function FormularioDirecto() {



  const DEV_DEMO = false; // ponlo en false cuando no lo ocupes

    const DEMO_SONG_ID = "tXXuPwCJaTzwcecZnfFJ";
    const [guestId] = useState(() => getOrCreateGuestId());

    const demoSongLive = {
      coverUrl: "https://tu-cdn-o-firebase/preview1.mp3",
      coverUrl02: "https://tu-cdn-o-firebase/preview2.mp3",
    };

  const loadDemoSong = () => {
  // 1) simula que ya existe songId
  setCreatedSongId(DEMO_SONG_ID);

  // 2) simula snapshot "ready"
  setSongLive({
    status: "ready",
    coverUrl: demoSongLive.coverUrl,
    coverUrl02: demoSongLive.coverUrl02,
  });

  // opcional: limpia error/busy
  setError("");
  setBusy(false);
};


  const { t, lang } = useLanguage();
  const navigate = useNavigate();

  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  //EVENTOS ANALITYC Y PIXEL FB

  const FB_IGNORE = new Set([
    "nextPaso1","nextPaso2","nextPaso3","nextPaso4","nextPaso5","nextPaso6","nextPaso7","nextPaso8","nextPaso9",
  ]);

  const track = (name, params = {}) => {
    // GA4
    try {
      if (window.gtag) window.gtag("event", name, params);
    } catch {}

    // FB Pixel (solo si NO está en ignore)
    try {
      if (window.fbq && !FB_IGNORE.has(name)) {
        const standard = ["PageView","ViewContent","Lead","AddToCart","InitiateCheckout","Purchase","CompleteRegistration"];
        if (standard.includes(name)) window.fbq("track", name, params);
        else window.fbq("trackCustom", name, params);
      }
    } catch {}
  };


  const [forcePending, setForcePending] = useState(false);
  const processingKeys = useMemo(() => ([
    "procesando.iniciando.tu.cancion",
    "procesando.validando.parametros",
    "procesando.dando.forma.a.la.idea",
    "procesando.interpretando.el.estilo.musical",
    "procesando.construyendo.la.melodia",
    "procesando.afinando.el.ritmo",
    "procesando.refinando.la.estructura",
    "procesando.aplicando.detalles.finales",
    "procesando.ultimos.ajustes",
    "procesando.finalizando",
  ]), []);

    // para step 9
  const [createdSongId, setCreatedSongId] = useState(null);
  const [userGuestId, setUserGuestId] = useState(null);
  const [songLive, setSongLive] = useState(null);
  const [starting, setStarting] = useState(false);

  const [loadingMsgIndex, setLoadingMsgIndex] = useState(0);
  const hasSongId = !!createdSongId;

  const status = songLive?.status || ""; // "" cuando todavía no llega snapshot

  const isPending = forcePending || ((hasSongId && status === "pending") || busy);
  const isReady = hasSongId && status === "ready";


  useEffect(() => {
    if (!isPending) return;

    setLoadingMsgIndex(0); // reinicia al entrar a pending

    const interval = setInterval(() => {
      setLoadingMsgIndex((prev) => {
        const next = prev + 1;
        return Math.min(prev + 1, processingKeys.length - 1); 
      });
    }, 20000); // ✅ 20 segundos

    return () => clearInterval(interval);
  }, [isPending, processingKeys.length]);




  // Timer de pago (ej: 12 horas)
  const FORM_EXPIRE_MS = 12 * 60 * 60 * 1000; // 12h

  const [expireAt, setExpireAt] = useState(() => {
    const existing = loadExpireAt();
    if (existing && existing > Date.now()) return existing;
    return 0; // aún no inicia
  });

  // iniciar 1 sola vez cuando el usuario entra al wizard (step 0)
  const startExpireIfNeeded = () => {
    setExpireAt((prev) => {
      if (prev && prev > Date.now()) return prev;
      const fresh = Date.now() + FORM_EXPIRE_MS;
      saveExpireAt(fresh);
      return fresh;
    });
  };

  const resetExpire = () => {
    clearExpireAt();
    setExpireAt(0);
  };



    const GUEST_SONGID_KEY = "guest_createdSongId_v1";
    const GUEST_ID_KEY = "guest_id_v1";
    const GUEST_EMAIL_KEY = "guest_email_v1";

    function saveGuestSession({ songId, guestId, email }) {
    try {
        if (songId) sessionStorage.setItem(GUEST_SONGID_KEY, songId);
        if (guestId) sessionStorage.setItem(GUEST_ID_KEY, guestId);
        if (email) sessionStorage.setItem(GUEST_EMAIL_KEY, email);
    } catch {}
    }

    function loadGuestSession() {
    try {
        return {
        songId: sessionStorage.getItem(GUEST_SONGID_KEY) || "",
        guestId: sessionStorage.getItem(GUEST_ID_KEY) || "",
        email: sessionStorage.getItem(GUEST_EMAIL_KEY) || "",
        };
    } catch {
        return { songId: "", guestId: "", email: "" };
    }
    }

    function useQuery() {
        const { search } = useLocation();
        return useMemo(() => new URLSearchParams(search), [search]);
    }

    const q = useQuery();

  const [draft, setDraft] = useState(() => loadDraft() || defaultDraft);


  // invitado: email para entrega
  const [guestEmail, setGuestEmail] = useState(() => {
    try { return sessionStorage.getItem("guest_email_v1") || ""; } catch { return ""; }
  });


  const [guestEmail2, setGuestEmail2] = useState(() => {
    try { return sessionStorage.getItem("guest_email2_v1") || ""; } catch { return ""; }
  });



  useEffect(() => {
    const songIdFromUrl = (q.get("songId") || "").trim();
    const session = loadGuestSession(); // { songId, guestId, email }

    const finalSongId = songIdFromUrl || (session.songId || "");

    // 1) Si hay songId final, la ponemos en state (una sola vez)
    if (finalSongId && !createdSongId) {
      setCreatedSongId(finalSongId);

      // 2) Refrescamos sessionStorage con el songId final
      //    y conservamos guestId/email que ya estaban guardados (si existen)
      saveGuestSession({
        songId: finalSongId,
        guestId: session.guestId || "",
        email: session.email || ""
      });
    }

  // 3) Recuperamos guestId/email a state si existen y aún no están en state
  if (session.guestId && !userGuestId) setUserGuestId(session.guestId);
  if (session.email && !guestEmail) setGuestEmail(session.email);

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  // límites invitado letras (step 7)
  const [guestLimits, setGuestLimits] = useState(() => loadGuestLimits());
  useEffect(() => {
    saveGuestLimits(guestLimits);
  }, [guestLimits]);

  useEffect(() => {
    try { sessionStorage.setItem("guest_email_v1", guestEmail || ""); } catch {}
  }, [guestEmail]);

  useEffect(() => {
    try { sessionStorage.setItem("guest_email2_v1", guestEmail2 || ""); } catch {}
}, [guestEmail2]);

const normalizeEmail = (v) => (v || "").trim().toLowerCase();
const isValidEmail = (v) => /^\S+@\S+\.\S+$/.test((v || "").trim());

const emailA = normalizeEmail(guestEmail);
const emailB = normalizeEmail(guestEmail2);

const emailOk = isValidEmail(guestEmail);
const email2Ok = isValidEmail(guestEmail2);
const emailsMatch = emailA && emailB && emailA === emailB;

const canSubmitGuestSong = !busy && !starting;
const canPay = emailOk && email2Ok && emailsMatch && starting;
const bienEmail = emailOk && email2Ok && emailsMatch;

  // autosave draft (debounce)
  const saveTimerRef = useRef(null);
  useEffect(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveDraft(draft);
    }, 350);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [draft]);

  const totalSteps = 10; // 0..9 (incluye step 9 música)
  const step = draft.step;

  const setField = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const goNext = () => setDraft((d) => ({ ...d, step: Math.min(d.step + 1, totalSteps - 1) }));
  const goBack = () => setDraft((d) => ({ ...d, step: Math.max(d.step - 1, 0) }));
  const goToStep = (n) => setDraft((d) => ({ ...d, step: Math.max(0, Math.min(n, totalSteps - 1)) }));

  const ideas = useMemo(() => {
    const lines = [
      `${draft.estilo?.trim() || "-"}`,
      draft.instrumento?.trim()
        ? `Instrumento: ${draft.instrumento.trim()}`
        : `Instrumento: -`,
      `Voz: ${draft.voz === "mujer" ? "femenina" : "masculina"}`,
      "",
      ` ${draft.ocasion?.trim() || "-"}`,
      ` ${draft.animo?.trim() || "-"}`,
      "",
      ``,
      `${draft.fechas?.trim() || "-"}`,
      "",
      ``,
      `${draft.eventos?.trim() || "-"}`,
      "",
      `Nombres:`,
      `${draft.nombres?.trim() || "-"}`,
      "",
      ``,
      `${draft.historia?.trim() || "-"}`,
      "",
      ``,
      `${draft.extras?.trim() || "-"}`
    ];
    return lines.join("\n");
  }, [draft]);

  const promptDefault = useMemo(() => {
    const parts = [
      `Estilo: ${draft.estilo?.trim() || "-"}`,
      draft.instrumento?.trim() ? `Instrumentos: ${draft.instrumento.trim()}` : null,
      `Voz: ${draft.voz === "mujer" ? "femenina" : "masculina"}`
    ].filter(Boolean);
    return parts.join(" | ");
  }, [draft.estilo, draft.instrumento, draft.voz]);

  const validateStep = () => {
    setError("");

    if (step === 1 && !draft.estilo.trim()) return t("songWizard.errors.requiredStyle");

    if (step === 7) {
      const len = ideas.trim().length;
      if (len < 130) return t("songWizard.errors.minChars");
    }
    return "";
  };

  const handleNext = () => {
    const msg = validateStep();
    if (msg) {
      setError(msg);
      return;
    }

    const paso = step + 1; // si step inicia en 0

    // ✅ evento simple
    track(`nextPaso${paso}`);

    goNext();
  };

  // guest anti-bot letras
  const guestLeft = guestLimits.left || 0;
  const msSinceLast = Date.now() - (guestLimits.lastAt || 0);
  const guestInCooldown = msSinceLast < GUEST_COOLDOWN_MS;
  const canGenerateAsGuest = guestLeft > 0 && !guestInCooldown;

  // monedas/precio (igual que tu Pay)
  const currencies = ["USD", "EUR", "BRL"];

  const defaultCurrency = useMemo(() => {
    return ["de", "fr"].includes(lang) ? "EUR" : lang === "pt" ? "BRL" : "USD";
  }, [lang]);

  const [currency, setCurrency] = useState(() => {
    return localStorage.getItem("pay_currency") || "AUTO";
  });

  const effectiveCurrency = currency === "AUTO" ? defaultCurrency : currency;

  useEffect(() => {
    localStorage.setItem("pay_currency", currency);
  }, [currency]);

  const prices = { USD: 15, EUR: 15, BRL: 60 };
  const amount = prices[effectiveCurrency] ?? 15;

  const priceLabel = useMemo(() => {
    try {
      return new Intl.NumberFormat(lang || "en", {
        style: "currency",
        currency: effectiveCurrency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    } catch {
      return effectiveCurrency === "EUR" ? `€${amount}` : effectiveCurrency === "BRL" ? `R$${amount}` : `$${amount} USD`;
    }
  }, [amount, effectiveCurrency, lang]);

  
  

  const handleGenerateLyrics = async () => {
    if (draft.letra?.trim()) {
      setError(t("songWizard.errors.alreadyGenerated"));
      return;
    }

    const len = ideas.trim().length;
    if (len < 130) {
      setError(t("songWizard.errors.minChars"));
      return;
    }

    // límites invitado
    if (guestLeft <= 0) {
      setError(t("songWizard.errors.guestLimit"));
      return;
    }
    if (guestInCooldown) {
      const secs = Math.ceil((GUEST_COOLDOWN_MS - msSinceLast) / 1000);
      setError(t("songWizard.errors.cooldown", { seconds: secs }));
      return;
    }

    track("generarLetraWeb");

    try {
      setBusy(true);
      setError("");
      setCreatedSongId(null);
      setSongLive(null);

   //   const { token } = await getToken(appCheck, false);

      const resp = await fetch(GENERAR_LETRA_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
    //      "X-Firebase-AppCheck": token
        },
        body: JSON.stringify({ idea: ideas, targetLang: lang })
      });

      let data = {};
      try {
        data = await resp.json();
      } catch {}

      if (!resp.ok) {
        throw new Error(data?.error || t("songWizard.errors.generic"));
      }

      const letra = (data?.letra || "").trim();

      // consumir intento invitado
      setGuestLimits((prev) => ({
        left: Math.max((prev.left || 0) - 1, 0),
        lastAt: Date.now()
      }));

      setDraft((d) => ({
        ...d,
        letra,
        prompt: d.prompt?.trim() ? d.prompt : promptDefault,
        generatedAt: Date.now(),
        step: 8
      }));
    } catch (e) {
      console.error("generateLyrics error:", e);
      setError(t("createSong.errors.generic"));
    } finally {
      setBusy(false);
    }
  };

  const copyLyrics = async () => {
    try {
      await navigator.clipboard.writeText(draft.letra || "");
      track("copiarLetraWeb");
      setError("");
      alert(t("songWizard.copied"));
    } catch {
      setError(t("songWizard.errors.generic"));
    }
  };

  const buildTitulo = () => {
    const base = draft.ocasion?.trim()
      ? `${draft.ocasion.trim()} - ${draft.estilo.trim() || "Canción"}`
      : draft.estilo.trim()
      ? `Canción ${draft.estilo.trim()}`
      : "Canción personalizada";
    return base.length > 60 ? base.slice(0, 60) : base;
  };
const GENERAR_CANCION_GUEST_URL =
  "https://us-central1-cancionesia.cloudfunctions.net/generarCancionGuestFromLyrics";
const GENERAR_CANCION_GUEST_URL0 =
  "https://us-central1-cancionesia.cloudfunctions.net/generarCancion";

  // crear canción como invitado (sin sesión)
  const finalizeCreateSongGuest = async () => {
    const emailNorm = guestEmail.trim().toLowerCase();

    
    if (!draft.letra.trim()) {
      setError(t("songWizard.errors.requiredLyrics"));
      return;
    }
    if (!draft.estilo.trim()) {
      setError(t("songWizard.errors.requiredStyle"));
      return;
    }

      // ✅ 1) validar email
      if (!emailNorm) {
        setError(t("formulario.escribe.un.correo.02"));
        return;
      }


   

    try {
      setBusy(true);
      setError("");

      const guestId = toGuestId(emailNorm);

       console.log("[UI] guestid:", guestId);

      // guests/{guestId}
      const guestRef = doc(db, "usuarios", "guests");

      const { songId: existingSongId } = loadGuestSession();
      if (existingSongId) {
        // si ya hay una, solo reengancha UI y sal
        setCreatedSongId(existingSongId);
        setUserGuestId(toGuestId(emailNorm));
        saveGuestSession({ songId: existingSongId, guestId: toGuestId(emailNorm), email: emailNorm });
        return;
      }

      // guests/{guestId}/canciones/{songId}
      const cancionesCol = collection(db, "usuarios", "guests", "canciones");
      const songRef = doc(cancionesCol);

      track("crearCancionWeb");

      await setDoc(
        guestRef,
        {
          guestId,
          email: emailNorm,
          createdAt: serverTimestamp(),
          lastSeenAt: serverTimestamp(),
          lastSongAt: serverTimestamp(),
          totalSongs: increment(1)
        },
        { merge: true }
      );

      const data = {
        id: songRef.id,
        guestId,
        email: emailNorm,
        desdeWeb: true,

        titulo: buildTitulo(),
        letra: draft.letra,
        estilo: draft.estilo.trim(),
        prompt: (draft.prompt?.trim() || promptDefault),

        currency: effectiveCurrency,
        amountSuggested: amount,

        pago: false,
        status: "pending",


        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(songRef, data, { merge: true });

       console.log("[UI] SONG ID: ", songRef.id);
       
       console.log("[UI] EMAIL: ", emailNorm);
       

      // mini doc para webhook / lectura simple (como ya lo usas)
      const miniRef = doc(db, "cancionesWeb", songRef.id);
      const miniData = {
        songId: songRef.id,
        usuarioId: "guests",
        guestId: guestId,
        shareLang: lang, 
        emailGuest: emailNorm,

        
        videoUrl: "",
        videoUrl02: "",
        desdeWeb: true,
        titulo: buildTitulo(),
        letra: draft.letra,
        estilo: draft.estilo.trim(),
        prompt: (draft.prompt?.trim() || promptDefault),

        status: "pending",

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(miniRef, miniData, { merge: true });

      console.log("[UI] canciones web paso SONG ID: ", songRef.id);

      const genero = draft.voz === "mujer" ? "female" : "male";
      const instrumental = false; // o un switch si lo agregas
      const tags = draft.estilo?.trim() || "pop";
      const title = buildTitulo();
      const promptMusic = (draft.prompt?.trim() || promptDefault);

      const r = await fetch(GENERAR_CANCION_GUEST_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idCancion: songRef.id,
          idUser: guestId,
          prompt: draft.letra,
          title: buildTitulo(),
          tags: (draft.prompt?.trim() || promptDefault || draft.estilo || "pop"),
          instrumental: false,
          genero: draft.voz === "mujer" ? "f" : "m",
          mv: "sonic-v5",
        }),
      });
      

      const out = await r.json().catch(() => ({}));
      if (!r.ok) {
        console.error("generarCancionGuestFromLyrics error:", out);
        setError("No se pudo iniciar la creación de la música. Intenta de nuevo.");
        return;
      }

      // para escuchar updates
      setCreatedSongId(songRef.id);
      setUserGuestId(guestId);
      saveGuestSession({ songId: songRef.id, guestId, email: emailNorm });
    } catch (e) {
      console.error("finalizeCreateSongGuest error:", e);
      setError(t("formulario.no.se.pudo"));
    } finally {
      setBusy(false);
    }
  };

  // escuchar cuando el webhook actualice coverUrl/ready
  useEffect(() => {
  if (!createdSongId) return;

  setSongLive(null);

  const ref = doc(db, "cancionesWeb", createdSongId);
  const unsub = onSnapshot(ref, (snap) => {
    if (!snap.exists()) {
      // songId ya no sirve
      setSongLive(null);
      setCreatedSongId(null);
      clearGuestSongId();
      return;
    }

    const live = { id: snap.id, ...snap.data() };
    const st = live?.status;

    // si status no es pending/ready -> limpiar songId y permitir crear otra
    if (st !== "pending" && st !== "ready") {
  //    setSongLive(null);
  //    setCreatedSongId(null);
  //    clearGuestSongId();
 //     return;
    }
    console.log("[UI] songlive status:", st);
     console.log("[UI] songlive status:", createdSongId);

    
    setSongLive(live);
  });

  return () => unsub();
}, [createdSongId]);







    // UI helpers
    const StepHeader = ({ title, text }) => (
      <>
        <h1 className="h1">{title}</h1>
        {text ? (
          <p className="p-muted" style={{ marginTop: 8 }}>
            {text}
          </p>
        ) : null}
        <p className="p-muted" style={{ marginTop: 8, fontSize: 12 }}>
          {t("songWizard.step", { current: step + 1, total: totalSteps })}
        </p>
      </>
    );

  const NavButtons = ({ showBack = true, showNext = true, nextLabel }) => (
    <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
      {showBack ? (
        <button className="btn" type="button" onClick={goBack} disabled={busy}>
          {t("songWizard.back")}
        </button>
      ) : null}

      {showNext ? (
        <button className="btn btn-primary" type="button" onClick={handleNext} disabled={busy}>
          {nextLabel || t("songWizard.next")}
        </button>
      ) : null}
    </div>
  );

  const AudioCard = ({ title, url }) => (
    <div
        style={{
        marginTop: 12,
        padding: 14,
        borderRadius: 14,
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.08)"
        }}
    >
        <div style={{ fontWeight: 800, marginBottom: 8 }}>{title}</div>

        {url ? (
        <audio
            controls
            controlsList="nodownload noplaybackrate noremoteplayback"
            disableRemotePlayback
            onContextMenu={(e) => e.preventDefault()}
            style={{ width: "100%" }}
            src={url}
        />
        ) : (
        <div className="p-muted">{t("formulario.aun.no.esta")}</div>
        )}
    </div>
    );

  // wizard render
  const renderWizardCard = () => {
    if (step === 0) {
      const basePrice =
        lang === "pt" ? "R$ 60" :
        lang === "fr" ? "15 €" :
        lang === "de" ? "15 €" :
        "USD$ 15";

      return (
        <section className="card">
          <StepHeader title={t("songWizard.step0Title")} />

          {/* Wrap visual (no agrega texto) */}
          <div style={{ marginTop: 10 }}>

           {/* Decor header: iconos + separador + texto */}
         <div style={{ marginBottom: 12 }}>
          {/* Row: icon + chip + icon */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              justifyContent: "space-between"
            }}
          >
            {/* 
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 14,
                display: "grid",
                placeItems: "center",
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.12)",
                boxShadow: "0 10px 24px rgba(0,0,0,0.22)"
              }}
            >
              🎵
            </div>
            Icon left */}

            {/* Text chip (más grande y pro) */}
            <div
              style={{
                padding: "10px 16px",
                borderRadius: 999,
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.12)",
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                fontSize: 16,
                lineHeight: 1.1,
                fontWeight: 950,
                letterSpacing: 0.2,
                boxShadow: "0 10px 26px rgba(0,0,0,0.18)"
              }}
            >
              <span style={{ color: "#F7D27D", textShadow: "0 0 18px rgba(247,210,125,0.18)" }}>✓</span>
              <span>{t("formulario.primero.escuchas")}</span>
            </div>

            {/*
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 14,
                display: "grid",
                placeItems: "center",
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.12)",
                boxShadow: "0 10px 24px rgba(0,0,0,0.22)"
              }}
            >
              ✨
            </div>
             Icon right */}
          </div>

          {/* Separator moved BELOW (siempre se ve) */}
          <div
            aria-hidden="true"
            style={{
              marginTop: 10,
              height: 2,
              width: "100%",
              borderRadius: 999,
              background:
                "linear-gradient(90deg, rgba(255,255,255,0.05), rgba(247,210,125,0.38), rgba(255,255,255,0.05))",
              boxShadow: "0 0 18px rgba(247,210,125,0.12)"
            }}
          />
        </div>

            {/* Card interior para el texto (mismo contenido) */}
            <div style={{
              padding: 14,
              borderRadius: 16,
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)"
            }}>
              <div
                className="step0Text"
                style={{
                  display: "grid",
                  gap: 10,
                  lineHeight: 1.55
                }}
              >
                <p style={{ margin: 0 }}>{t("songWizard.step0P1")}</p>

                {/* Bloque de precio resaltado (mismo texto) */}
               {/* Bloque de precio resaltado */}
              <div
                style={{
                  padding: 14,
                  borderRadius: 16,
                  background: "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
                  border: "1px solid rgba(255,255,255,0.12)",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                  position: "relative",
                  overflow: "hidden"
                }}
              >
                {/* brillo decorativo */}
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    inset: -80,
                    background:
                      "radial-gradient(circle at 30% 30%, rgba(255,215,130,0.18), transparent 55%), radial-gradient(circle at 70% 60%, rgba(255,255,255,0.10), transparent 55%)",
                    pointerEvents: "none"
                  }}
                />

                {/* Row: label + price */}
                <div
                  style={{
                    position: "relative",
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap"
                  }}
                >
                  <div style={{ fontWeight: 900 }}>
                    {t("songWizard.precioBase")}
                  </div>

                  <div
                    style={{
                      fontSize: 24,
                      fontWeight: 950,
                      letterSpacing: 0.2,
                      color: "#F7D27D",
                      textShadow: "0 0 18px rgba(247,210,125,0.22)"
                    }}
                  >
                    {basePrice}
                  </div>
                </div>

                {/* Line A */}
                <div
                  style={{
                    position: "relative",
                    marginTop: 8,
                    lineHeight: 1.45,
                    opacity: 0.95
                  }}
                >
                  {t("songWizard.step0PriceLineA")}
                </div>

              
              </div>

               
              </div>
            </div>

            {/* Botón con layout más pro (sin cambiar texto) */}
            <div style={{
              marginTop: 14,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap"
            }}>
              {/* Chip decorativo, sin texto extra */}
              <div style={{
                display: "flex",
                gap: 8
              }}>
                <span style={{
                  width: 10,
                  height: 10,
                  borderRadius: 999,
                  background: "rgba(255,255,255,0.30)"
                }} />
                <span style={{
                  width: 10,
                  height: 10,
                  borderRadius: 999,
                  background: "rgba(255,255,255,0.18)"
                }} />
                <span style={{
                  width: 10,
                  height: 10,
                  borderRadius: 999,
                  background: "rgba(255,255,255,0.10)"
                }} />
              </div>

              <button
                className="btn btn-primary"
                type="button"
                onClick={() => {
                  track("iniciarFormularioWeb");
                  startExpireIfNeeded();
                  goNext();
                }}
                style={{
                  marginTop: 0,
                  padding: "12px 16px",
                  borderRadius: 14,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10
                }}
              >
                <span>{t("songWizard.start")}</span>
                <span style={{ opacity: 0.9 }}>➜</span>
              </button>
            </div>
          </div>
        </section>
      );
    }

    if (step === 1) {
      return (
        <section className="card">
          <StepHeader title={t("songWizard.step1Title")} />
          <div className="form" style={{ marginTop: 10 }}>
            <label className="label">
              {t("songWizard.styleLabel")} *
              <input
                className="input"
                value={draft.estilo}
                onChange={(e) => setField("estilo", e.target.value)}
                placeholder={t("songWizard.stylePlaceholder")}
                maxLength={80}
              />
            </label>

            <label className="label">
              {t("songWizard.instrumentLabel")}
              <input
                className="input"
                value={draft.instrumento}
                onChange={(e) => setField("instrumento", e.target.value)}
                placeholder={t("songWizard.instrumentPlaceholder")}
                maxLength={80}
              />
            </label>

            <div className="label">
              {t("songWizard.voiceLabel")}
              <div className="voiceRow">
                <button
                  type="button"
                  className={`voiceChip ${draft.voz === "hombre" ? "active" : ""}`}
                  onClick={() => setField("voz", "hombre")}
                >
                  {t("songWizard.voiceMale")}
                </button>
                <button
                  type="button"
                  className={`voiceChip ${draft.voz === "mujer" ? "active" : ""}`}
                  onClick={() => setField("voz", "mujer")}
                >
                  {t("songWizard.voiceFemale")}
                </button>
              </div>
            </div>

            {error ? <div className="alert-error">{error}</div> : null}
            <NavButtons />
          </div>
        </section>
      );
    }

    if (step === 2) {
      return (
        <section className="card">
          <StepHeader title={t("songWizard.step2Title")} />
          <div className="form" style={{ marginTop: 10 }}>
            <label className="label">
              {t("songWizard.occasionLabel")}
              <input
                className="input"
                value={draft.ocasion}
                onChange={(e) => setField("ocasion", e.target.value)}
                placeholder={t("songWizard.occasionPlaceholder")}
                maxLength={100}
              />
            </label>
            {error ? <div className="alert-error">{error}</div> : null}
            <NavButtons />
          </div>
        </section>
      );
    }

    if (step === 3) {
      return (
        <section className="card">
          <StepHeader title={t("songWizard.step3Title")} />
          <div className="form" style={{ marginTop: 10 }}>
            <label className="label">
              {t("songWizard.moodLabel")}
              <input
                className="input"
                value={draft.animo}
                onChange={(e) => setField("animo", e.target.value)}
                placeholder={t("songWizard.moodPlaceholder")}
                maxLength={100}
              />
            </label>
            <NavButtons />
          </div>
        </section>
      );
    }

    if (step === 4) {
      return (
        <section className="card">
          <StepHeader title={t("songWizard.step4Title")} />
          <div className="form" style={{ marginTop: 10 }}>
            <label className="label">
              {t("songWizard.datesLabel")}
              <textarea
                className="input textarea"
                value={draft.fechas}
                onChange={(e) => setField("fechas", e.target.value)}
                placeholder={t("songWizard.datesPlaceholder")}
                rows={6}
              />
            </label>
            <label className="label">
              {t("songWizard.datesExtraLabel")}
              <textarea
                className="input textarea"
                value={draft.eventos}
                onChange={(e) => setField("eventos", e.target.value)}
                placeholder={t("songWizard.datesExtraPlaceholder")}
                rows={3}
              />
            </label>
            <NavButtons />
          </div>
        </section>
      );
    }

    if (step === 5) {
      return (
        <section className="card">
          <StepHeader title={t("songWizard.step5Title")} />
          <div className="form" style={{ marginTop: 10 }}>
            <label className="label">
              {t("songWizard.namesLabel")}
              <textarea
                className="input textarea"
                value={draft.nombres}
                onChange={(e) => setField("nombres", e.target.value)}
                placeholder={t("songWizard.namesPlaceholder")}
                rows={5}
              />
              <span className="help-text">{t("songWizard.namesHelp")}</span>
            </label>
            <NavButtons />
          </div>
        </section>
      );
    }

    if (step === 6) {
      return (
        <section className="card">
          <StepHeader title={t("songWizard.step6Title")} />
          <div className="form" style={{ marginTop: 10 }}>
            <label className="label">
              {t("songWizard.storyLabel")}
              <textarea
                className="input textarea"
                value={draft.historia}
                onChange={(e) => setField("historia", e.target.value)}
                placeholder={t("songWizard.storyPlaceholder")}
                rows={7}
              />
            </label>
            <NavButtons />
          </div>
        </section>
      );
    }

   if (step === 7) {
    const alreadyHasLyrics = !!draft.letra.trim(); // ✅ ya hay letra

    return (
        <section className="card">
        <StepHeader title={t("songWizard.step7Title")} />
        <div className="form" style={{ marginTop: 10 }}>
            <label className="label">
            {t("songWizard.extraLabel")}
            <textarea
                className="input textarea"
                value={draft.extras}
                onChange={(e) => setField("extras", e.target.value)}
                placeholder={t("songWizard.extraPlaceholder")}
                rows={6}
            />
            </label>

            {error ? <div className="alert-error">{error}</div> : null}

            <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
            <button className="btn" type="button" onClick={goBack} disabled={busy}>
                {t("songWizard.back")}
            </button>

            {!alreadyHasLyrics ? (    
            <p className="note" style={{ marginTop: 0 }}>
              {t("formulario.si.quieres.cambiar")}            
            </p>
            ) : null}

            
            <button
                className="btn btn-primary"
                type="button"
                onClick={handleGenerateLyrics}
                disabled={busy || alreadyHasLyrics || !canGenerateAsGuest}
            >
                {busy ? t("songWizard.generatingLyrics") : t("songWizard.generateLyrics")}
            </button>

            {/* ✅ Si ya existe letra, mostrar acceso directo */}
            {alreadyHasLyrics ? (                 

                <button
                className="btn"
                type="button"
                onClick={() => goToStep(8)}
                disabled={busy}
                title= {t("formulario.ver.la.letra")}  
                >
                {t("formulario.ir.a.mi")}    
                </button>
            ) : null}
            </div>

            {!alreadyHasLyrics ? (
            <p className="p-muted" style={{ marginTop: 8, fontSize: 12 }}>
                {t("songWizard.guestLeft", { count: guestLeft })}
            </p>
            ) : (
            <p className="p-muted" style={{ marginTop: 8, fontSize: 12 }}>
              {t("formulario.ya.tienes.una")}  
            </p>
            )}

            {busy ? (
            <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center" }}>
                <div className="spinner" />
                <p style={{ margin: 0 }} className="p-muted">
                {t("songWizard.generatingText")}
                </p>
            </div>
            ) : null}
 {DEV_DEMO ? (
            <button
                className="btn btn-primary"
                type="button"
                onClick={() => goToStep(8)}
              >
                IR MUSICA SIN LETRA
              </button>
              ) : null}
              
        </div>
        </section>
    );
    }

    // STEP 8: letra + prompt + nota + boton poner musica
    if (step === 8) {
      return (
        <section className="card">
          <StepHeader title={t("songWizard.step8Title")} />

          {error ? <div className="alert-error" style={{ marginTop: 10 }}>{error}</div> : null}

          <div className="form" style={{ marginTop: 10 }}>
            <label className="label">
              {t("songWizard.lyricsLabel")}
              <textarea
                className="input textarea"
                value={draft.letra}
                onChange={(e) => setField("letra", e.target.value)}
                placeholder={t("songWizard.lyricsPlaceholder")}
                rows={10}
              />
            </label>

            <label className="label" style={{ marginTop: 12 }}>
              {t("songWizard.promptLabel")}
              <textarea
                className="input textarea"
                value={draft.prompt}
                onChange={(e) => setField("prompt", e.target.value)}
                placeholder={promptDefault}
                rows={2}
              />
              <span className="help-text">{t("songWizard.promptHelp")}</span>
            </label>

            <p className="note" style={{ marginTop: 10 }}>
              {t("formulario.puedes.ajustar.la")}
            </p>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
              <button className="btn" type="button" onClick={copyLyrics} disabled={!draft.letra.trim()}>
                {t("songWizard.copyLyrics")}
              </button>
              
              <button className="btn" type="button" onClick={goBack} disabled={busy}>
                {t("songWizard.back")}
              </button>

              <button
                className="btn btn-primary"
                type="button"
                onClick={() => goToStep(9)}
                disabled={busy || !draft.letra.trim() || !draft.estilo.trim()}
              >
                {t("formulario.ponerle.musica")}
              </button>

         {DEV_DEMO ? (
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => goToStep(9)}
              >
                IR MUSICA SIN LETRA
              </button>
              ) : null}
            </div>
          </div>
        </section>
      );
    }

    // STEP 9: musica + correo + precio + crear cancion + loader + reproductor + pagar
    

    const disableCreate = busy || starting || isPending || isReady;

    const emailNorm = normalizeEmail(guestEmail);
    const emailNorm2 = normalizeEmail(guestEmail2);

    const songIdForPay = createdSongId || ""; // ✅ ÚNICA fuente
    const returnUrl = `${window.location.origin}/formulario?status=return&songId=${songIdForPay}`;

    // ✅ tu docId basado en email (para Firestore / backend)
    const guestDocId = toGuestId(emailNorm);

    const canPay =
      isValidEmail(emailNorm) &&
      isValidEmail(emailNorm2) &&
      emailNorm === emailNorm2 &&
      !!songIdForPay &&
      !starting;
    const showCreateButton = !hasSongId && !isPending && !isReady;

    return (
      <section className="card">
        <StepHeader title={t("formulario.crea.tu.cancion")} />
        
        {error ? <div className="alert-error" style={{ marginTop: 10 }}>{error}</div> : null}

        <div className="form" style={{ marginTop: 10 }}>
         

          <div style={{ marginTop: 10 }}>

          <label className="label">
            {t("formulario.correo.electronico.para")} *
            <input
              className="input"
              type="email"
              value={guestEmail}
              onChange={(e) => setGuestEmail(e.target.value)}
              placeholder={t("formulario.tu_correo.ejemplo")}
            />
          </label>

            <label className="label" style={{ marginTop: 10 }}>
              {t("formulario.confirmar.correo")}
            <input
                className="input"
                type="email"
                value={guestEmail2}
                onChange={(e) => {
                setGuestEmail2(e.target.value);
                setError("");
                }}
                placeholder={t("formulario.repite.tu.correo")}
                autoComplete="email"
                inputMode="email"
            />
            {/* feedback en vivo */}
            {guestEmail2.trim() ? (
                emailsMatch ? (
                <div className="p-muted" style={{ marginTop: 6, fontSize: 12 }}>
                    {t("formulario.los.correos.coinciden")}
                </div>
                ) : (
                <div className="alert-error" style={{ marginTop: 6 }}>
                    {t("formulario.los.correos.no")}
                </div>
                )
            ) : null}
            </label>
            
           
          {showCreateButton ? (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
              
              <button className="btn" type="button" onClick={goBack} disabled={busy || starting}>
                            {t("songWizard.back")}
              </button>

              <button
                className="btn btn-primary"
                type="button"
                onClick={finalizeCreateSongGuest}
                disabled={!canSubmitGuestSong || disableCreate}
              >
                {busy ? t("formulario.creando.cancion") : t("formulario.crea.tu.cancion") }
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
              <button className="btn" type="button" onClick={goBack} disabled={busy || starting}>
                {t("songWizard.back")}
              </button>

              <button className="btn btn-primary" type="button" disabled>
                {isPending ? t("formulario.creando.cancion") : "Canción lista"}
              </button>
            </div>
          )}

          {DEV_DEMO ? (
            <button
              type="button"
              className="btn"
              onClick={loadDemoSong}
              disabled={busy || starting}
              style={{ marginTop: 10 }}
            >
              Cargar canción DEMO (DEV)
            </button>
          ) : null}

         
{/* 
          <button
            type="button"
            className="btn"
            onClick={() => setForcePending(prev => !prev)}
            style={{ marginTop: 10 }}
          >
            {forcePending ? "Quitar Pending (DEV)" : "Forzar Pending (DEV)"}
          </button>
*/}
          {/* Ready: reproductores + pagar */}
          {isReady ? (
            <>
              <div style={{ marginTop: 12, fontWeight: 800 }}>
                 {t("formulario.listo.tus.dos")}
              </div>

              <AudioCard title={t("formulario.version.uno")} url={songLive.coverUrl} />
              <AudioCard title={t("formulario.version.dos")} url={songLive.coverUrl02} />

              <div className="note" style={{ marginTop: 10 }}>
                {t("formulario.recibiras.las.dos")}
              </div>

              {!bienEmail ? (
                <div className="alert-error" style={{ marginTop: 10 }}>
                  {t("formulario.escribe.tu.correo.02")}
                </div>
              ) : null}
              {error ? <div className="alert-error" style={{ marginTop: 10 }}>{error}</div> : null}


              <button
                className="btn btn-primary"
                type="button"
                onClick={() => {
                  const emailNorm = guestEmail.trim().toLowerCase();
                  const guestDocId = toGuestId(emailNorm);
                  const songId = createdSongId;

                  // validaciones rápidas
                  if (!songId) return alert("Primero crea tu canción");
                  if (!emailNorm) return alert("Escribe tu correo");

                  track("pagarCancionWeb");

                  const qs = new URLSearchParams({
                    songId,
                    email: emailNorm,
                    guestId: guestDocId,
                    currency: effectiveCurrency,
                  });

                  navigate(`/pagoguest?${qs.toString()}`);
                }}
                disabled={!canPay}
                style={{ marginTop: 12 }}
              >
                {t("formulario.pagar")}
              </button>
              
            </>
          ) : null}

        

          {/* Loader mientras llega ready */}
          {isPending ? (
            <div style={{
              marginTop: 14,
              padding: 14,
              borderRadius: 14,
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)"
            }}>
              <div style={{ fontWeight: 900, marginBottom: 6 }}>
                {t(processingKeys[loadingMsgIndex])}
              </div>
              <div className="p-muted" style={{ marginTop: -2, fontSize: 12 }}>
                {t("procesando.paso", { current: loadingMsgIndex + 1, total: processingKeys.length })}
              </div>
              <div className="p-muted">
                {t("formulario.tu.cancion.estara")}           
              </div>
              <div style={{ marginTop: 10, display: "flex", gap: 10, alignItems: "center" }}>
                <div className="spinner" />
                <div className="p-muted" style={{ margin: 0 }}>
                  {t("formulario.esperando.respuesta")}   
                </div>
              </div>
            </div>
          ) : null}

          <div className="divider" />
          <div className="priceCard">
            <div className="priceTop">
              <div>
                <div className="priceLabel">{t("pay.priceLabel", "Precio")}</div>

                <div className="priceRow">
                  {/*
                    <div className="priceOld">
                      {t("pay.priceOldLabel", "Antes")}{" "}
                      <span className="priceOldValue">
                        {t("pay.priceOldValue", "—")}
                      </span>
                    </div>
                    */}

                  <div className="priceBadge">
                    {t("songWizard.step0PriceLineA")}
                  </div>
                </div>

                <div className="priceMain">{priceLabel}</div>

                <div className="priceHint">
                  {t("pay.flexibleHint", "En la pasarela de pago puedes elegir la cantidad. Tú decides.")}
                </div>
              </div>

              <div className="priceIcon">💳</div>
            </div>

            <div className="priceDivider" />

            <div className="currencyBox">
              <div className="currencyLeft">
                <div className="currencyTitle">{t("pay.currency", "Moneda")}</div>
                <div className="currencySub">
                  {t("pay.currencyHint", "Puedes cambiar la moneda si lo prefieres.")}
                </div>
              </div>

              <select
                className="input"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                style={{ maxWidth: 220 }}
              >
                <option value="AUTO">{t("pay.currencyAuto", "Automática")}</option>
                {currencies.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          
          </div>

          
        </div>
      </section>
    );
  };

  const renderSummaryCard = () => {
    return (

    
      <section className="card">   
      
        {step >= 9 ? (
          <CountdownBanner t={t} expireAt={expireAt} onReset={resetExpire} />
        ) : null}
        
        <h2 className="h2">{t("songWizard.summaryTitle")}</h2>
        <p className="p-muted">{t("songWizard.summarySubtitle")}</p>

        <div className="summaryBox">
          <div className="summaryRow">
            <span className="summaryLabel">{t("songWizard.summaryStyle")}</span>
            <span className="summaryValue">{draft.estilo.trim() || "--"}</span>
          </div>
          <div className="summaryRow">
            <span className="summaryLabel">{t("songWizard.summaryInstrument")}</span>
            <span className="summaryValue">{draft.instrumento.trim() || "--"}</span>
          </div>
          <div className="summaryRow">
            <span className="summaryLabel">{t("songWizard.summaryVoice")}</span>
            <span className="summaryValue">
              {draft.voz === "mujer" ? t("songWizard.voiceFemale") : t("songWizard.voiceMale")}
            </span>
          </div>
          <div className="summaryRow">
            <span className="summaryLabel">{t("songWizard.summaryIdeas")}</span>
            <span className="summaryValue">{ideas.trim() ? "✅" : "--"}</span>
          </div>
          <div className="summaryRow">
            <span className="summaryLabel">{t("songWizard.summaryLyrics")}</span>
            <span className="summaryValue">{draft.letra.trim() ? "✅" : "--"}</span>
          </div>
        </div>

        <div className="summaryPrompt">
          <span className="summaryLabel">{t("songWizard.summaryPrompt")}</span>
          <div className="promptBox">{draft.prompt?.trim() || promptDefault}</div>
        </div>
 {/*
        <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            className="btn"
            type="button"
            onClick={() => {
              setDraft(defaultDraft);
              clearDraft();
              resetGuestLimits();
              setCreatedSongId(null);
              setSongLive(null);
              setError("");
            }}
          >
            {t("songWizard.reset")}
          </button>

          <button
            className="btn"
            type="button"
            onClick={() => {
              // útil para pruebas: volver directo a step 9 si ya tienen letra
              if (draft.letra.trim()) goToStep(9);
              else alert("Primero genera la letra.");
            }}
          >
            Ir a música
          </button>
        </div>
        */}
      </section>
    );
  };

  return (
    <div>
    
      {/* Top bar */}
      <div className="lp" style={{ minHeight: "auto" }}>
        <header className="lp-topbar">
          <div className="lp-container lp-topbar-inner">
            <div className="lp-brand">
              <img className="lp-mark-img" src={logoMark} alt={`${t("app.name")} logo`} />
              <span className="lp-brand-name">{t("app.name")}</span>
            </div>

            <LanguageDropdown size="md" showLabel align="left" />             
          </div>
        </header>
      </div>
      <div className="page">
        <div className="container">
          <div className="grid-2">
            {renderWizardCard()}
            {renderSummaryCard()}
          </div>
        </div>
      </div>
      
    </div>

  );
}